import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CaseStudy {
  id: number;
  title: string;
  client: string;
  category: string;
  description: string;
  metrics: { value: string; label: string }[];
  image: string;
  bgColor: string;
  style: 'A' | 'B' | 'C';
  externalLink?: string;
}

export default function CaseStudies() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      title: 'Netflix UX/UI Case Study (Personal Project)',
      client: 'MediCare Solutions',
      category: 'Product Design',
      description: 'Reimagining patient experience through intuitive interface design and streamlined workflows',
      metrics: [
        { value: '45%', label: 'Faster Booking' },
        { value: '92%', label: 'User Satisfaction' },
        { value: '3.2M', label: 'Active Users' }
      ],
      image: 'https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/12cbeae069bb3100f7704443821b2233.jpeg',
      bgColor: '#0A1628',
      style: 'A'
    },
    {
      id: 2,
      title: 'Tonal Fitness, The Reinvention',
      client: 'NexBank',
      category: 'Product Design',
      description: '',
      metrics: [],
      image: 'https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/8b61a105e6f53fe759170b0dd93d0975.png',
      bgColor: '#FF6B4A',
      style: 'B'
    },
    {
      id: 3,
      title: 'The Home Depot',
      client: 'EduTech Global',
      category: 'UX Design',
      description: 'Enhancing the retail experience with intuitive navigation and seamless checkout flows',
      metrics: [
        { value: '68%', label: 'Completion Rate' },
        { value: '4.8', label: 'App Rating' },
        { value: '120K', label: 'Students' }
      ],
      image: 'https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/6e385a07cad0cf1f51953fd45e6fa07f.png',
      bgColor: '#F8F6F3',
      style: 'C',
      externalLink: 'https://drive.google.com/file/d/1NFTTvHqKQV68F5BdmWf4ltQAr3FVp0uy/view?usp=drive_link'
    },
    {
      id: 6,
      title: 'Chick-fil-a Audit (Personal Project)',
      client: 'FitLife Pro',
      category: 'Product Design',
      description: 'Comprehensive health monitoring with personalized workout recommendations',
      metrics: [],
      image: 'https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/216169bbbe9a7c5e55b6f7e7bc994412.png',
      bgColor: '#84A98C',
      style: 'A',
      externalLink: 'https://drive.google.com/file/d/1S-QhmGso09wYpO1mgCuViRnVFY5hqMY-/view?usp=sharing'
    },
    {
      id: 5,
      title: 'Social Media Project',
      client: 'QuickBite',
      category: 'Service Design',
      description: '',
      metrics: [
        { value: '35%', label: 'Faster Delivery' },
        { value: '89%', label: 'Order Accuracy' },
        { value: '2.1M', label: 'Monthly Orders' }
      ],
      image: 'https://public.readdy.ai/ai/img_res/d3f7093a214b4620dd254279cd20e9cc.jpg',
      bgColor: '#F8F6F3',
      style: 'C',
      externalLink: 'https://drive.google.com/file/d/1ABk10qf4UJIzfcH9Eu2bUvd0tjo_zGIX/view?usp=drive_link'
    },
    {
      id: 7,
      title: 'Alaskan Airlines Design System',
      client: 'Wanderlust',
      category: 'UX Design',
      description: '',
      metrics: [
        { value: '72%', label: 'Booking Increase' },
        { value: '4.9', label: 'User Rating' },
        { value: '850K', label: 'Trips Planned' }
      ],
      image: 'https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/9d4494906f547b2417afb405a6377c59.png',
      bgColor: '#F8F6F3',
      style: 'C',
      externalLink: 'https://drive.google.com/drive/folders/13VBld5kcQydqpemv8CwGGMHM2DyPJt0S?usp=drive_link'
    },
    {
      id: 8,
      title: 'Project 7',
      client: '',
      category: 'Coming Soon',
      description: 'New project coming soon',
      metrics: [],
      image: 'https://readdy.ai/api/search-image?query=minimal%20abstract%20geometric%20shapes%20soft%20gradient%20background%20clean%20modern%20design%20placeholder%20concept%20art%20professional%20aesthetic%20light%20gray%20white%20tones%20subtle%20texture&width=700&height=500&seq=8&orientation=landscape',
      bgColor: '#F8F6F3',
      style: 'C'
    }
  ];

  const handleCardClick = (study: CaseStudy) => {
    if (study.externalLink) {
      window.open(study.externalLink, '_blank');
    } else {
      navigate(`/project/${study.id}`);
    }
  };

  const renderCard = (study: CaseStudy) => {
    const isHovered = hoveredCard === study.id;

    if (study.style === 'A') {
      return (
        <div
          className={`relative rounded-[40px] overflow-hidden shadow-lg transition-all duration-300 cursor-pointer aspect-square ${
            isHovered ? 'scale-[1.02] shadow-2xl' : ''
          }`}
          onMouseEnter={() => setHoveredCard(study.id)}
          onMouseLeave={() => setHoveredCard(null)}
          onClick={() => handleCardClick(study)}
        >
          <div className="relative w-full h-full">
            <img
              src={study.image}
              alt={study.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            
            <div className="absolute top-6 right-6">
              <span className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-light">
                {study.category}
              </span>
            </div>

            <div className="absolute bottom-8 left-8 right-8">
              <h3 className="text-white text-3xl font-bold mb-2">
                {study.title}
              </h3>
            </div>
          </div>
        </div>
      );
    }

    if (study.style === 'B') {
      return (
        <div
          className={`rounded-[40px] bg-white border border-gray-200 p-8 shadow-lg transition-all duration-300 cursor-pointer aspect-square flex flex-col ${
            isHovered ? 'scale-[1.02] shadow-2xl' : ''
          }`}
          onMouseEnter={() => setHoveredCard(study.id)}
          onMouseLeave={() => setHoveredCard(null)}
          onClick={() => handleCardClick(study)}
        >
          <div className="text-[#FF6B4A] text-xs font-medium tracking-wide mb-6 uppercase">
            {study.category}
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {study.title}
          </h3>
          
          <div className="relative w-full flex-1 rounded-3xl overflow-hidden">
            <img
              src="https://static.readdy.ai/image/ed7d6e9501624c265bc1ac340fb9283d/b3a61f9c5a49f5bc8e5623fd88822496.png"
              alt={study.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={`rounded-[40px] bg-white border border-gray-200 p-8 shadow-lg transition-all duration-300 cursor-pointer aspect-square flex flex-col ${
          isHovered ? 'scale-[1.02] shadow-2xl' : ''
        }`}
        onMouseEnter={() => setHoveredCard(study.id)}
        onMouseLeave={() => setHoveredCard(null)}
        onClick={() => handleCardClick(study)}
      >
        <div className="text-[#FF6B4A] text-xs font-medium tracking-wide mb-6 uppercase">
          {study.category}
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-8 leading-tight">
          {study.title}
        </h3>
        
        {study.description && (
          <div className="mb-8">
            <p className="text-gray-700 text-sm leading-relaxed">
              {study.description}
            </p>
          </div>
        )}

        <div className="relative w-full flex-1 rounded-3xl overflow-hidden">
          <img
            src={study.image}
            alt={study.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    );
  };

  return (
    <section id="work" className="py-16 md:py-24 px-6 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {caseStudies.map((study) => (
            <div key={study.id}>
              {renderCard(study)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
